<template>
  <div class="bg-white dark:bg-bs-dark-bg-over rounded-3xl tip-item p-5 mt-4 md:mt-6">
    <h3 class="font-bold text-bs-dark-bg dark:text-white text-lg mb-3">Equity Impact</h3>

    <div class="flex justify-between items-center mb-3 pb-3 border-b border-bs-gray-light dark:border-bs-dark-bg">
      <span class="font-medium text-bs-gray-dark dark:text-white">Change</span>
      <span v-if="outcome" :class="['status-' + status.toLowerCase(), 'text-right']">
        <h3 class="value main-value font-semibold">
          {{ outcome.value >= 0 ? '+' : '' }}{{ formatCurrency(outcome.value) }}
        </h3>
        <h5 class="value text-xs">
          <span>{{ outcome.symbol }}</span>
          {{ outcome.percent ? outcome.percent.toLocaleString('en-GB', { style: 'percent', maximumFractionDigits: 2 }) : '' }}
        </h5>
      </span>
    </div>
    <div v-if="outcome" class="flex justify-between items-center mb-3 pb-3 border-b border-bs-gray-light dark:border-bs-dark-bg">
      <span class="font-medium text-bs-gray-dark dark:text-white">Equity after</span>
      <span class="font-bold text-lg text-bs-dark-bg dark:text-white">{{ formatCurrency(outcome.total + accaOffsetAtTip) }}</span>
    </div>

    <div class="px-2 mb-3">
      <svg viewBox="0 0 200 50" :class="['line-chart', 'line-status-' + status.toLowerCase()]">
        <g>
          <path :d="area" class="area" :fill="`url(#gradient-${status.toLowerCase()}-${id})`" />
          <path :d="line" fill="transparent" stroke-width="2" class="line" stroke-linecap="round" />
          <circle r="4" :cx="circle.x - 2" :cy="circle.y - 2" />
        </g>
        <defs>
          <linearGradient :id="`gradient-${status.toLowerCase()}-${id}`" x1="0" x2="0" y1="0" y2="1">
            <stop class="stop1" offset="3%" />
            <stop class="stop2" offset="60%" />
          </linearGradient>
        </defs>
      </svg>
    </div>

    <div class="flex justify-between w-full pt-2">
      <span class="font-medium text-bs-gray-dark dark:text-white">Handicap</span>
      <span class="font-bold text-xl gradient-text">{{ handicap || '—' }}</span>
    </div>
    <div class="flex justify-between w-full pt-3" v-if="outcome">
      <span class="font-medium text-bs-gray-dark dark:text-white">Stake</span>
      <span class="font-bold text-xl gradient-text">{{ formatCurrency(outcome.stake) }}</span>
    </div>
    <div class="flex justify-between w-full pt-3">
      <span class="font-medium text-bs-gray-dark dark:text-white">Odd</span>
      <span class="font-bold text-xl gradient-text">{{ oddValue }}</span>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import { scaleTime, scaleLinear, extent, line, area } from "d3";
import { addDays, isBefore } from "date-fns";
export default {
  props: ["id", "status", "handicap", "oddValue", "createdDate"],
  computed: {
    ...mapGetters(["equityByDate", "settings", "tips"]),
    ...mapState({
      accaHistory: (state) => state.accumulators.history,
    }),
    stakePct() {
      return ((this.settings && this.settings.stakeAmount) || 0) / 100;
    },
    accaOffsetAtTip() {
      // Cumulative acca P/L applied through this tip's createdDate (inclusive)
      const tipTs = new Date(this.createdDate).getTime();
      const settledAccas = (this.accaHistory || [])
        .filter((a) => (a.status === 1 || a.status === 2) && a.settledDate && new Date(a.settledDate).getTime() <= tipTs)
        .map((a) => ({
          ts: new Date(a.settledDate).getTime(),
          status: a.status,
          odds: Number(a.effectiveOdds != null ? a.effectiveOdds : a.totalOdds),
        }))
        .sort((a, b) => a.ts - b.ts);
      if (!settledAccas.length) return 0;

      const tipPoints = (this.tips || [])
        .filter((t) => t.status !== "PENDING" && t.virtualEquityAfterTip !== null)
        .map((t) => ({ ts: new Date(t.createdDate).getTime(), type: "tip", equityAfter: t.virtualEquityAfterTip }))
        .filter((p) => p.ts <= tipTs);
      const accaPoints = settledAccas.map((a) => ({ ts: a.ts, type: "acca", status: a.status, odds: a.odds }));
      const merged = [...tipPoints, ...accaPoints].sort((a, b) => a.ts - b.ts);

      let offset = 0;
      let lastEquity = (this.settings && this.settings.startingEquity) || 0;
      for (const e of merged) {
        if (e.type === "tip") {
          lastEquity = e.equityAfter + offset;
        } else {
          const stake = lastEquity * this.stakePct;
          const result = e.status === 1 && e.odds > 0 ? stake * (e.odds - 1) : -stake;
          offset += result;
          lastEquity += result;
        }
      }
      return offset;
    },
    line() {
      if (!this.equityByDate || !this.equityByDate.length) return "";
      const x = this.x();
      const y = this.y();
      const lineFn = line()
        .x((d) => x(d.x))
        .y((d) => y(d.y));
      const d = addDays(new Date(this.createdDate), 1);
      return lineFn(this.equityByDate.filter((item) => isBefore(item.x, d)));
    },
    area() {
      if (!this.equityByDate || !this.equityByDate.length) return "";
      const x = this.x();
      const y = this.y();
      const fn = area()
        .x((d) => x(d.x))
        .y0(() => y(0))
        .y1((d) => y(d.y));
      const d = addDays(new Date(this.createdDate), 1);
      return fn(this.equityByDate.filter((item) => isBefore(item.x, d)));
    },
    outcome() {
      if (!this.equityByDate) return null;
      const bucket = this.equityByDate.find((d) => d.tipsOutcome && d.tipsOutcome[this.id]);
      if (!bucket) return null;
      const o = bucket.tipsOutcome[this.id];
      o.percent = o.total ? o.value / o.total : 0;
      o.symbol = o.value > 0 ? "▴" : o.value < 0 ? "▾" : "";
      return o;
    },
    circle() {
      if (!this.outcome) return { x: 0, y: 0 };
      const x = this.x();
      const y = this.y();
      return { x: x(new Date(this.createdDate)), y: y(this.outcome.total) };
    },
  },
  methods: {
    x() {
      return scaleTime()
        .domain(extent(this.equityByDate, (d) => new Date(d.x)))
        .range([5, 180]);
    },
    y() {
      return scaleLinear()
        .domain(extent(this.equityByDate, (d) => d.y))
        .range([45, 10]);
    },
    formatCurrency(n) {
      return (
        typeof n !== "undefined" &&
        n.toLocaleString("en-GB", {
          style: "currency",
          currency: this.settings?.currencyCode || "EUR",
          currencyDisplay: "symbol",
        })
      );
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/base.scss";

.tip-item {
  box-shadow: 0px 7px 14px #dde2ef;
}
.dark .tip-item {
  box-shadow: 0px 7px 14px rgba(0, 0, 0, 0.1);
}

.line-status-success path.line { stroke: transparentize($forest-green, 0.5); }
.line-status-halfwin path.line { stroke: transparentize($forest-green-light, 0.5); }
.line-status-void path.line { stroke: transparentize($orange, 0.5); }
.line-status-halfloss path.line { stroke: transparentize($light-red, 0.5); }
.line-status-fail path.line { stroke: transparentize($red-red, 0.5); }

.line-chart .stop1 { stop-opacity: 0.3; }
.line-chart .stop2 { stop-opacity: 0; }
.line-status-success .stop1 { stop-color: $forest-green; }
.line-status-success .stop2 { stop-color: $forest-green; }
.line-status-halfwin .stop1 { stop-color: $forest-green; }
.line-status-halfwin .stop2 { stop-color: $forest-green; }
.line-status-void .stop1 { stop-color: $orange; }
.line-status-void .stop2 { stop-color: $orange; }
.line-status-halfloss .stop1 { stop-color: $light-red; }
.line-status-halfloss .stop2 { stop-color: $light-red; }
.line-status-fail .stop1 { stop-color: $red-red; }
.line-status-fail .stop2 { stop-color: $red-red; }

.line-status-success circle, .status-success .value { fill: $forest-green; color: $forest-green; }
.line-status-halfwin circle, .status-halfwin .value { fill: $forest-green-light; color: $forest-green-light; }
.line-status-void circle, .status-void .value { fill: $orange; color: $orange; }
.line-status-halfloss circle, .status-halfloss .value { fill: $light-red; color: $light-red; }
.line-status-fail circle, .status-fail .value { fill: $red-red; color: $red-red; }

.dark .status-success .main-value, .dark .status-halfwin .main-value { background: $forest-green; @apply rounded px-3 text-white; }
.dark .status-void .main-value { background: $orange; @apply rounded px-3 text-white; }
.dark .status-halfloss .main-value, .dark .status-fail .main-value { background: $red-red; @apply rounded px-3 text-white; }
.dark .value { color: white; }

.gradient-text {
  background: linear-gradient(90deg, #3555FE, #3EA3FC);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
}
</style>
